import React, { useState, useEffect } from 'react'
import Footer from '../components/Footer';
import PrivacyPopup from '../components/PrivacyPopup';
import TnCPopup from '../components/TnCPopup';
import api from '../config/api';
import { Link, useNavigate } from 'react-router-dom';
import { executeRecaptcha } from '../utils/recaptcha';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_STORAGE_KEY } from '../i18n';
import {
  INVALID_NAME_MESSAGE,
  isValidIndianMobile,
  isValidSafeName,
  normalizeIndianMobile,
  normalizeName,
  sanitizeNameInput,
} from '../utils/authValidation';
import {
  applyServerRateLimit,
  AUTH_RATE_LIMIT,
  consumeRateLimit,
} from '../utils/clientRateLimit';
import {
  trackSignupStart,
  trackLanguageSelect,
  trackSignupFieldInteract,
  trackSignupValidationError,
  trackConsentCheckboxTap,
  trackStateSelect,
  trackSignupSubmit,
  trackOtpRequested,
} from '../utils/ga4Events';
import { getUtmParams } from '../utils/utmCapture';
import { trackAtcConversion } from '../utils/marketingPixels';
import { shouldRedirectToWelcomeScreen } from '../utils/profileRedirect';
// import LanguageToggle from '../components/LanguageToggle';

const AVAILABLE_STATES = [
  {
    value: "Tamil Nadu",
    labelEn: "Tamil Nadu",
    labelTa: "தமிழ்நாடு",
  },
  {
    value: "Kerala",
    labelEn: "Kerala",
    labelTa: "கேரளா",
  },
];

const getAnalyticsLanguageLabel = (languageCode) =>
  languageCode === "ta" ? "Tamil" : "English";

function Registeration() {
  const { t, i18n } = useTranslation();
  const [screen, setScreen] = useState("lang");

  // Fire signup_start once when the form screen is shown
  useEffect(() => {
    if (screen === 'signup') {
      const { utm_source, utm_medium, utm_campaign } = getUtmParams();
      trackSignupStart({ source: utm_source, utm_medium, utm_campaign });
    }
  }, [screen]);
  const [fieldErrors, setFieldErrors] = useState({
    phone: false,
    name: false,
  });
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [language, setLanguage] = useState(i18n.language === "ta" ? "ta" : "en");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    state: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [showTncPopup, setShowTncPopup] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const redirectExistingProfile = async () => {
      const shouldRedirect = await shouldRedirectToWelcomeScreen();

      if (isMounted && shouldRedirect) {
        navigate("/welcome-back", { replace: true });
      }
    };

    redirectExistingProfile();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handlePhoneFocus = (e) => {
    // Explicitly focus and select the input to ensure keyboard opens on iOS
    e.target.focus();
  };

  const isTamil = i18n.language === "ta";

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const sanitized = sanitizeNameInput(value);
      setForm((prev) => ({ ...prev, name: sanitized }));

      const normalized = normalizeName(sanitized);
      const isValid = isValidSafeName(normalized);

      setFieldErrors((prev) => ({ ...prev, name: !isValid }));
      return;
    }

    if (name === "phone") {
      const normalized = normalizeIndianMobile(value);
      setForm((prev) => ({ ...prev, phone: normalized }));

      setFieldErrors((prev) => ({ ...prev, phone: false }));

      return;
    }

    if (name === "state") {
      setForm((prev) => ({ ...prev, state: value }));
      setErrorMessage("");
      trackStateSelect({ state: value, screen_name: "signup" });
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrorMessage("");

    // Clear phone error on change
    if (name === "phone") {
      setFieldErrors((prev) => ({ ...prev, phone: false }));
    }
  };

  const handleLanguageChange = (value) => {
    setLanguage(value);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, value);
    i18n.changeLanguage(value);
    trackLanguageSelect({ language: getAnalyticsLanguageLabel(value) });
  };

  const handleLanguageConfirm = () => {
    const selectedLanguage = language || "en";

    localStorage.setItem(LANGUAGE_STORAGE_KEY, selectedLanguage);
    i18n.changeLanguage(selectedLanguage);
    trackLanguageSelect({ language: getAnalyticsLanguageLabel(selectedLanguage) });
    setScreen("signup");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const normalizedName = normalizeName(form.name);
    const normalizedPhone = normalizeIndianMobile(form.phone);

    setFieldErrors({
      name: false,
      phone: false,
    });

    if (!isValidSafeName(normalizedName)) {
      setFieldErrors((prev) => ({ ...prev, name: true }));
      setErrorMessage(INVALID_NAME_MESSAGE);
      trackSignupValidationError({ field_name: 'name', error_type: 'invalid_name' });
      return;
    }

    // Validate phone
    if (!isValidIndianMobile(normalizedPhone)) {
      setFieldErrors((prev) => ({ ...prev, phone: true }));
      setErrorMessage(t("auth.invalidPhone"));
      setForm((prev) => ({ ...prev, phone: "" }));
      trackSignupValidationError({ field_name: 'phone', error_type: 'invalid_phone' });
      return;
    }

    if (!agreed) {
      setErrorMessage(t("auth.agreeError"));
      trackSignupValidationError({ field_name: 'consent', error_type: 'consent_required' });
      return;
    }

    if (!form.state) {
      setErrorMessage(t("auth.stateError"));
      trackSignupValidationError({ field_name: 'state', error_type: 'state_required' });
      return;
    }

    setFieldErrors((prev) => ({ ...prev, phone: false }));
    trackSignupSubmit({ state: form.state, has_consent: agreed });

    try {
      const rateLimitStatus = consumeRateLimit(AUTH_RATE_LIMIT);

      if (rateLimitStatus.blocked) {
        setErrorMessage(AUTH_RATE_LIMIT.message);
        return;
      }

      setLoading(true);
      const recaptchaToken = await executeRecaptcha("signup");
      const { utm_source, utm_medium, utm_campaign } = getUtmParams();
      const payload = {
        name: normalizedName,
        mobile: normalizedPhone,
        state: form.state,
        token: recaptchaToken,
        flow: "signup",
        utm_source,
        utm_medium,
        utm_campaign,
        selectedLanguage: getAnalyticsLanguageLabel(language), // Tamil or English
      };
      await api.post("/user/register", payload);
      trackOtpRequested();
      trackAtcConversion();
      localStorage.setItem("mobile", normalizedPhone)
      localStorage.setItem("userState", form.state)
      navigate("/otp-verification")
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 429) {
        applyServerRateLimit(
          AUTH_RATE_LIMIT,
          error?.response?.headers?.["retry-after"]
        );
      }
      setErrorMessage(
        error?.response?.data?.message || t("auth.registrationFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="main-wrapper">
          <div className="frame-outer mobile-only">
            <span className="frame-corner tl" />
            <span className="frame-corner tr" />
            <span className="frame-corner bl" />
            <span className="frame-corner br" />
            <div className="frame-border-outer">
              <div className="frame-border">
                <div className="frame-border-inner">
                  <div className="frame-canvas">
                    <img
                      className="frame-bg-image"
                      src="assets/images/star-bg.webp"
                      alt="Star Element"
                    />

                    {/* LANGUAGE SELECT POPUP */}
                    {screen === "lang" && (
                      <div id="lang-select" className="frame-content">
                        <img
                          src="assets/images/splash-content.webp"
                          className="lang-bg-image"
                          alt="Splash Content"
                        />
                        <div className="lang-screen">
                          <div className="lang-scroll-wrap">
                            <div className="lang-scroll">
                              <div className="lang-scroll-inner">
                                <img
                                  src="assets/images/language-popup-bg.webp"
                                  className="img-fit"
                                  alt="Language Background"
                                />
                                <p
                                  className="lang-heading"
                                  style={{
                                    fontSize: language === "ta" ? "14px" : "18px"
                                  }}
                                >
                                  {t("common.chooseYourLanguage").split("\n")[0]}
                                  <br /> {t("common.chooseYourLanguage").split("\n")[1]}
                                </p>
                                <div className="lang-options">
                                  <label className="lang-option">
                                    <input
                                      type="radio"
                                      name="language"
                                      value="ta"
                                      checked={language === "ta"}
                                      onChange={(e) => handleLanguageChange(e.target.value)}
                                      className="lang-radio"
                                    />
                                    <span className="lang-radio-custom" />
                                    <span className="lang-label">தமிழ்</span>
                                  </label>
                                  <label className="lang-option">
                                    <input
                                      type="radio"
                                      name="language"
                                      value="en"
                                      checked={language === "en"}
                                      onChange={(e) => handleLanguageChange(e.target.value)}
                                      className="lang-radio"
                                    />
                                    <span className="lang-radio-custom" />
                                    <span className="lang-label">English</span>
                                  </label>
                                </div>
                              </div>
                              <div className="lang-scroll-footer">
                                <button
                                  className="btn-next"
                                  onClick={handleLanguageConfirm}
                                >
                                  <span>{t("common.okay")}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SIGN UP SCREEN */}
                    {screen === "signup" && (
                      <div id="main" className="frame-content">
                        <div className="signup-screen">
                          <div className="signup-container">
                            <div className="signup-header">
                              <div className="signup-logo-wrap">
                                <img src="assets/images/animate-britania-logo.webp" alt="Britannia" className="signup-logo signup-logo-1" />

                                <img src="assets/images/animate-milk-bikkis-logo.webp" alt="Milk Bikis" className="signup-logo signup-logo-2" />
                              </div>

                              <img src="assets/images/new-signup-logo.webp" alt="திருக்குறள் சேலஞ்ச்" className="signup-title-img" />
                            </div>

                            <div className="signup-body">
                              <h2 className="signup-heading text-center" style={{ marginBottom: "20px" }}>{t("auth.signUp")}</h2>
                              {/* <p className="signup-subtext">
                                {t("auth.completeKurals").split("\n")[0]}
                                <br />
                                {t("auth.completeKurals").split("\n")[1]}
                              </p> */}

                              {/* Global error message */}

                              <div className="cashback-banner">
                                <div className="banner-content">
                                  <h3
                                    style={{
                                      fontSize: isTamil ? "7px" : "10px",
                                      lineHeight: isTamil ? "1.15" : "1.3",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {t("banner.cashbackTitle")}
                                    <br />
                                    {t("banner.cashbackSubtitle")}
                                  </h3>

                                  <img
                                    src="assets/images/bicycle-icon.png"
                                    alt={t("banner.cashbackSubtitle")}
                                  />
                                </div>
                              </div>
                              <form
                                className="signup-form"
                                id="signupForm"
                                onSubmit={handleSubmit}
                              >
                                {/* Name */}
                                <div className={`form-field input-with-icon${fieldErrors.name ? " error" : ""}`}>
                                  <input
                                    type="text"
                                    name="name"
                                    placeholder={t("auth.name")}
                                    className="form-input"
                                    value={form.name}
                                    onChange={handleChange}
                                    onFocus={() => trackSignupFieldInteract({ field_name: 'name' })}
                                    maxLength={50}
                                  />

                                  {fieldErrors.name && (
                                    <>
                                      <span className="error-text">{INVALID_NAME_MESSAGE}</span>
                                      <span className="error-icon">!</span>
                                    </>
                                  )}
                                </div>

                                {/* Phone Number */}
                                <div
                                  className={`form-field input-with-icon${fieldErrors.phone ? " error" : ""}`}
                                  id="phoneField"
                                >
                                  <input
                                    type="tel"
                                    name="phone"
                                    placeholder={t("auth.phoneNumber")}
                                    className="form-input"
                                    id="phone"
                                    maxLength={10}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    inputMode="numeric"
                                    value={form.phone}
                                    onChange={handleChange}
                                    onFocus={(e) => { handlePhoneFocus(e); trackSignupFieldInteract({ field_name: 'phone' }); }}
                                  />
                                  {fieldErrors.phone && (
                                    <>
                                      <span className="error-text">{t("auth.invalidPhone")}</span>
                                      <span className="error-icon">!</span>
                                    </>
                                  )}
                                </div>

                                {/* State Dropdown */}
                                <div className="form-field">
                                  <div className="form-select-wrap">
                                    <select
                                      className="form-input form-select"
                                      name="state"
                                      value={form.state}
                                      onChange={handleChange}
                                      onFocus={() => trackSignupFieldInteract({ field_name: 'state' })}
                                      required
                                    >
                                      <option value="" disabled>{t("auth.state")}</option>
                                      {AVAILABLE_STATES.map((stateOption) => (
                                        <option key={stateOption.value} value={stateOption.value}>
                                          {language === "ta" ? stateOption.labelTa : stateOption.labelEn}
                                        </option>
                                      ))}
                                    </select>
                                    <span className="select-arrow">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={16}
                                        height={16}
                                        viewBox="0 0 44 44"
                                        fill="none"
                                      >
                                        <path
                                          d="M35.1185 16.2085L21.6114 29.7156L8.10425 16.2085"
                                          stroke="black"
                                          strokeWidth="4.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </span>
                                  </div>
                                </div>

                                {/* Checkbox */}
                                <div className="form-checkbox-row">
                                  <input
                                    type="checkbox"
                                    id="agree"
                                    className="form-checkbox"
                                    checked={agreed}
                                    onChange={(e) => {
                                      trackConsentCheckboxTap({ already_checked: agreed });
                                      setAgreed(e.target.checked);
                                    }}
                                    required
                                  />
                                  <label htmlFor="agree" className="form-checkbox-label">
                                    {t("auth.agreeText")}
                                    <br />
                                    <Link
                                      to="/term-and-condition"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setShowTncPopup(true);
                                      }}
                                    >
                                      {t("common.terms")}
                                    </Link>{" "}
                                    {t("common.and")}{" "}
                                    <Link
                                      to="/privacy-policy"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setShowPrivacyPopup(true);
                                      }}
                                    >
                                      {t("common.privacy")}
                                    </Link>
                                  </label>
                                </div>

                                {errorMessage && (
                                  <p style={{ color: "red", fontSize: "10px", textAlign: "center", marginBottom: "2px" }}>
                                    {errorMessage}
                                  </p>
                                )}

                                {/* reCAPTCHA */}
                                {/* <div className="form-field recaptcha-field">
                                  <div className="recaptcha-placeholder">Protected by reCAPTCHA</div>
                                </div> */}

                                {/* Submit Button */}
                                <button
                                  style={{ marginTop: '12px' }}
                                  className="btn-next"
                                  type="submit"
                                  disabled={loading}
                                >
                                  <span style={{ fontSize: language === "ta" ? '14px' : '18px' }}>{loading ? t("common.pleaseWait") : t("common.next")}</span>
                                </button>

                                {/* Login */}
                                <p className="signup-login">
                                  {t("auth.alreadyRegistered")}{language === 'ta' && <br />} <Link to="/login">{t("auth.logInLink")}</Link>
                                </p>
                              </form>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      <TnCPopup
        isOpen={showTncPopup}
        onClose={() => setShowTncPopup(false)}
        showCloseButton={true}
      />
      <PrivacyPopup
        isOpen={showPrivacyPopup}
        onClose={() => setShowPrivacyPopup(false)}
        showCloseButton={true}
      />
    </>
  );
}

export default Registeration;
